import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Layout, BookOpen, Trash2 } from 'lucide-react';
import { Tooltip } from '../../components/Tooltip';
import { cn } from '../../utils/cn';
import type { Blog, BlogStats } from '../../schemas/validation';

interface BlogData {
  blog: Blog;
  stats: BlogStats;
}

interface AppsSectionProps {
  siteId: string;
  blogData: BlogData | null;
  isBlogLoading: boolean;
  isInstallingBlog: boolean;
  onInstallBlog: () => void;
  onUninstallClick: () => void;
}

export const AppsSection: React.FC<AppsSectionProps> = ({
  siteId,
  blogData,
  isBlogLoading,
  isInstallingBlog,
  onInstallBlog,
  onUninstallClick,
}) => {
  const navigate = useNavigate();

  return (
    <div className="mb-8">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-3">
          <Layout className="h-6 w-6 text-gray-400" />
          <h2 className="text-lg font-medium text-gray-900">Apps</h2>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Blog App Card */}
        <div className="card p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center shadow-sm">
                <BookOpen className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Blog</h3>
                <p className="text-sm text-gray-500">Create and publish articles</p>
              </div>
            </div>
          </div>

          {isBlogLoading ? (
            <div className="mt-4 flex items-center justify-center py-4">
              <div className="w-5 h-5 spinner"></div>
            </div>
          ) : blogData ? (
            <div className="mt-4 space-y-3">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-gray-50 rounded-lg py-2">
                  <div className="text-lg font-semibold text-gray-900">
                    {blogData.stats.totalPosts}
                  </div>
                  <div className="text-xs text-gray-500">Posts</div>
                </div>
                <div className="bg-gray-50 rounded-lg py-2">
                  <div className="text-lg font-semibold text-green-600">
                    {blogData.stats.publishedPosts}
                  </div>
                  <div className="text-xs text-gray-500">Published</div>
                </div>
                <div className="bg-gray-50 rounded-lg py-2">
                  <div className="text-lg font-semibold text-yellow-600">
                    {blogData.stats.draftPosts}
                  </div>
                  <div className="text-xs text-gray-500">Drafts</div>
                </div>
              </div>

              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Status:</span>
                <span
                  className={cn(
                    'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                    blogData.blog.isPublished
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  )}
                >
                  {blogData.blog.isPublished ? 'Published' : 'Unpublished'}
                </span>
              </div>

              <div className="flex items-center space-x-2 pt-2">
                <button
                  onClick={() => navigate(`/sites/${siteId}/blog`)}
                  className="flex-1 btn-primary inline-flex items-center justify-center space-x-2"
                >
                  <Edit size={16} />
                  <span>Manage Blog</span>
                </button>
                <Tooltip content="Uninstall Blog">
                  <button
                    onClick={onUninstallClick}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 size={18} />
                  </button>
                </Tooltip>
              </div>
            </div>
          ) : (
            <div className="mt-4 space-y-3">
              <p className="text-sm text-gray-600">
                Add a blog to your website to share news, articles, and updates with your visitors.
              </p>
              <button
                onClick={onInstallBlog}
                disabled={isInstallingBlog}
                className="w-full btn-primary inline-flex items-center justify-center space-x-2"
              >
                {isInstallingBlog ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Installing...</span>
                  </>
                ) : (
                  <>
                    <Plus size={16} />
                    <span>Install Blog</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
